/**
 * Merges a SignalR message into a collection array (immutable).
 *
 * Modes:
 *  - Create / Update: pass the incoming object as `incoming`.
 *    Appends if the id doesn't exist, replaces if it does AND the
 *    incoming timestamp is newer (or timestamps are absent).
 *  - Delete: pass `options.deleteId` — removes the matching item.
 *
 * @param {Array}  collection    The current array (feedbacks, goals, etc.)
 * @param {Object} incoming      The message payload (ignored when deleteId is set)
 * @param {Object} [options]
 * @param {string} [options.idField='id']              Key used to match items
 * @param {string} [options.timestampField='createdAt'] Key used for freshness guard
 * @param {*}      [options.deleteId]                   When set, removes the item with this id
 * @returns {Array} A new array — original is never mutated
 */
export default function mergeSignalRMessage(collection, incoming, options = {}) {
    const { idField = 'id', timestampField = 'createdAt', deleteId } = options;
    const items = Array.isArray(collection) ? collection : [];

    // ── Delete mode ──
    if (deleteId !== undefined) {
        const index = items.findIndex((item) => item[idField] === deleteId);
        if (index === -1) {
            return items;
        }
        return items.filter((item) => item[idField] !== deleteId);
    }

    // ── Create / Update mode ──
    if (!incoming || incoming[idField] == null) {
        return items;
    }

    const existingIndex = items.findIndex((item) => item[idField] === incoming[idField]);

    // New item — append
    if (existingIndex === -1) {
        return [...items, incoming];
    }

    // Existing item — check timestamp freshness
    const existing = items[existingIndex];
    if (
        timestampField
        && existing[timestampField] != null
        && incoming[timestampField] != null
        && incoming[timestampField] <= existing[timestampField]
    ) {
        return items; // Stale message, ignore
    }

    // Replace the matched item
    return items.map((item, i) => (i === existingIndex ? incoming : item));
}
